/**
 * broadcastBridgeEvent (2026-05-23)
 *
 * Safe wrapper for AvatarBridge.broadcastEvent() — workflow handlers
 * outside React (src/services/agenticWorkflows/handlers.ts, etc.) call
 * this to push progress/completion/clarification events to the avatar.
 *
 * Thread id resolution (in order):
 *   1. args.threadId passed explicitly
 *   2. AvatarBridge's registered activeThreadIdGetter
 *   3. The last-known thread id captured by captureActiveThreadId() — a
 *      global setter that React-side code calls whenever the active thread
 *      changes, so workflow handlers running between React renders still
 *      have a non-null thread id.
 *
 * When none of the above yield a thread id, we STILL broadcast with
 * threadId = 'unknown' instead of silently dropping the event. The avatar
 * side will narrate it anyway (it's a system notification, not scoped to a
 * specific thread from the user's perspective — they just hear about the
 * workflow progress).
 */

import AvatarBridge, { type BridgeEventName } from '../avatarBridge/AvatarBridge';

export interface BroadcastArgs {
  event: BridgeEventName;
  threadId?: string;
  payload?: Record<string, unknown>;
}

let lastKnownThreadId: string | null = null;

/**
 * In-buffer event record — each broadcast appends one, keyed by thread id.
 * Used by the polling bridge method `getWorkflowEvents` so the avatar can
 * retrieve recent events even if the push channel (postMessage) is broken.
 * TTL 30 min; per thread keeps newest ~50 events.
 */
export interface BufferedBridgeEvent {
  event: BridgeEventName;
  threadId: string;
  payload: Record<string, unknown>;
  timestamp: string;
}

const EVENT_BUFFER_TTL_MS = 30 * 60 * 1000;
const EVENT_BUFFER_MAX_PER_THREAD = 50;
const eventBuffer = new Map<string, BufferedBridgeEvent[]>();

function pruneBuffer() {
  const cutoff = Date.now() - EVENT_BUFFER_TTL_MS;
  for (const [threadId, events] of eventBuffer) {
    const fresh = events.filter((e) => Date.parse(e.timestamp) >= cutoff);
    if (fresh.length === 0) {
      eventBuffer.delete(threadId);
    } else if (fresh.length > EVENT_BUFFER_MAX_PER_THREAD) {
      eventBuffer.set(threadId, fresh.slice(fresh.length - EVENT_BUFFER_MAX_PER_THREAD));
    } else {
      eventBuffer.set(threadId, fresh);
    }
  }
}

/** Read recent buffered events for one or more thread ids. */
export function readBufferedEvents(opts: {
  threadIds?: string[];
  sinceMs?: number;
}): BufferedBridgeEvent[] {
  const { threadIds, sinceMs } = opts;
  const cutoff = sinceMs ?? (Date.now() - EVENT_BUFFER_TTL_MS);
  const all: BufferedBridgeEvent[] = [];
  const ids = threadIds && threadIds.length > 0 ? threadIds : Array.from(eventBuffer.keys());
  for (const tid of ids) {
    const list = eventBuffer.get(tid);
    if (!list) continue;
    for (const e of list) {
      if (Date.parse(e.timestamp) >= cutoff) all.push(e);
    }
  }
  all.sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp));
  return all;
}

/**
 * React-side code calls this on every active-thread change. Captured at the
 * moment the thread switches, so it's a fresh value when workflow handlers
 * run on subsequent message dispatches.
 *
 * Wired via a useEffect in src/components/GlobalChatSystem/GlobalChatSystem.tsx
 * that listens to threadStore.activeThreadId.
 */
export function captureActiveThreadId(threadId: string | null): void {
  lastKnownThreadId = threadId;
}

export function broadcastBridgeEvent(args: BroadcastArgs): void {
  try {
    const threadId =
      args.threadId
      || AvatarBridge.getActiveThreadId()
      || lastKnownThreadId
      || 'unknown';
    // Always buffer + broadcast, even if threadId resolves to 'unknown'.
    // Silently dropping events was the bug — the avatar would see a
    // successful submit_to_thread and then get no narration at all because
    // the platform-side emissions disappeared. The buffer lets the avatar
    // poll for events as a fallback when the push fails.
    const timestamp = new Date().toISOString();
    const record: BufferedBridgeEvent = {
      event: args.event,
      threadId,
      payload: args.payload || {},
      timestamp,
    };
    const bucket = eventBuffer.get(threadId) || [];
    bucket.push(record);
    if (bucket.length > EVENT_BUFFER_MAX_PER_THREAD) {
      bucket.splice(0, bucket.length - EVENT_BUFFER_MAX_PER_THREAD);
    }
    eventBuffer.set(threadId, bucket);
    pruneBuffer();
    // eslint-disable-next-line no-console
    console.log('[broadcastBridgeEvent]', args.event, 'threadId=', threadId);
    AvatarBridge.broadcastEvent({
      event: args.event,
      threadId,
      payload: args.payload || {},
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[broadcastBridgeEvent] failed:', e);
  }
}
